import React from 'react';
import {
    Box,
    TextField,
    InputAdornment,
    IconButton,
    Typography,
    Chip,
    Stack,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import { inkColors } from './constants/inkColors';

const LimitedTierlistFilters = ({
    searchTerm,
    selectedRarity,
    selectedInkColor,
    selectedSet,
    selectedCost,
    showFilters,
    uniqueSets,
    uniqueRarities,
    uniqueCosts,
    commonUncommonOnly,
    onSearchChange,
    onRarityChange,
    onInkColorChange,
    onSetChange,
    onCostChange,
    onToggleFilters,
    onCommonUncommonChange
}) => {
    const handleClearAllFilters = () => {
        onSearchChange('');
        onRarityChange('all');
        onInkColorChange('all');
        onSetChange('all');
        onCostChange('all');
        onCommonUncommonChange(false);
    };

    const hasActiveFilters = searchTerm || selectedRarity !== 'all' || selectedInkColor !== 'all' || selectedSet !== 'all' || (selectedCost && selectedCost !== 'all') || commonUncommonOnly;

    return (
        <Box mb={3}>
            {/* Search bar */}
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mb={2}>
                <TextField
                    sx={{
                        flexGrow: 1,
                        minWidth: { xs: 200, sm: 300 },
                        '& .MuiInputBase-input': {
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                        }
                    }}
                    variant="outlined"
                    placeholder="Szukaj kart po nazwie lub podtytule..."
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                            <InputAdornment position="end">
                                <IconButton onClick={() => onSearchChange('')} size="small">
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                        sx: {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '12px',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(76, 29, 149, 0.3)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(76, 29, 149, 0.5)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#c4b5fd',
                            }
                        }
                    }}
                />
                <IconButton
                    onClick={onToggleFilters}
                    sx={{
                        background: showFilters ? 'rgba(76, 29, 149, 0.3)' : 'rgba(76, 29, 149, 0.1)',
                        border: '1px solid rgba(76, 29, 149, 0.3)',
                        borderRadius: '12px',
                        color: 'white',
                        '&:hover': {
                            background: 'rgba(76, 29, 149, 0.4)',
                        },
                    }}
                >
                    <FilterListIcon />
                </IconButton>
            </Box>

            {/* Active filters display */}
            {hasActiveFilters && (
                <Box sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {searchTerm && (
                            <Chip
                                label={`Szukaj: "${searchTerm}"`}
                                onDelete={() => onSearchChange('')}
                                size="small"
                                sx={{
                                    backgroundColor: 'rgba(76, 29, 149, 0.3)',
                                    color: 'white',
                                    '& .MuiChip-deleteIcon': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        )}
                        {selectedRarity !== 'all' && (
                            <Chip
                                label={`Rzadkość: ${selectedRarity}`}
                                onDelete={() => onRarityChange('all')}
                                size="small"
                                sx={{
                                    backgroundColor: 'rgba(76, 29, 149, 0.3)',
                                    color: 'white',
                                    '& .MuiChip-deleteIcon': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        )}
                        {selectedInkColor !== 'all' && (
                            <Chip
                                label={`Kolor: ${inkColors[selectedInkColor]?.name || selectedInkColor}`}
                                onDelete={() => onInkColorChange('all')}
                                size="small"
                                sx={{
                                    backgroundColor: 'rgba(76, 29, 149, 0.3)',
                                    color: 'white',
                                    '& .MuiChip-deleteIcon': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        )}
                        {selectedSet !== 'all' && (
                            <Chip
                                label={`Set: ${selectedSet}`}
                                onDelete={() => onSetChange('all')}
                                size="small"
                                sx={{
                                    backgroundColor: 'rgba(76, 29, 149, 0.3)',
                                    color: 'white',
                                    '& .MuiChip-deleteIcon': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        )}
                        {selectedCost && selectedCost !== 'all' && (
                            <Chip
                                label={`Koszt: ${selectedCost}`}
                                onDelete={() => onCostChange('all')}
                                size="small"
                                sx={{
                                    backgroundColor: 'rgba(76, 29, 149, 0.3)',
                                    color: 'white',
                                    '& .MuiChip-deleteIcon': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        )}
                        {commonUncommonOnly && (
                            <Chip
                                label="C+UC"
                                onDelete={() => onCommonUncommonChange(false)}
                                size="small"
                                sx={{
                                    backgroundColor: 'rgba(76, 29, 149, 0.3)',
                                    color: 'white',
                                    '& .MuiChip-deleteIcon': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        )}
                        <Chip
                            label="Wyczyść wszystkie"
                            onClick={handleClearAllFilters}
                            size="small"
                            sx={{
                                backgroundColor: 'rgba(239, 68, 68, 0.3)',
                                color: 'white',
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.5)' }
                            }}
                        />
                    </Stack>
                </Box>
            )}

            {/* Advanced filters */}
            {showFilters && (
                <Box sx={{
                    p: 2,
                    background: 'rgba(76, 29, 149, 0.05)',
                    borderRadius: '12px',
                    border: '1px solid rgba(76, 29, 149, 0.2)',
                    mb: 2
                }}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontSize: '1rem' }}>
                        Filtry
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={commonUncommonOnly}
                                    onChange={(e) => onCommonUncommonChange(e.target.checked)}
                                    sx={{
                                        color: 'rgba(76, 29, 149, 0.5)',
                                        '&.Mui-checked': {
                                            color: '#c4b5fd',
                                        }
                                    }}
                                />
                            }
                            label="C+UC"
                            sx={{
                                color: 'white',
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '0.875rem'
                                }
                            }}
                        />
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Rzadkość</InputLabel>
                            <Select
                                value={selectedRarity}
                                label="Rzadkość"
                                onChange={(e) => onRarityChange(e.target.value)}
                                sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(76, 29, 149, 0.3)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(76, 29, 149, 0.5)',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#c4b5fd',
                                    }
                                }}
                            >
                                <MenuItem value="all">Wszystkie rzadkości</MenuItem>
                                {uniqueRarities.map(rarity => (
                                    <MenuItem key={rarity} value={rarity}>{rarity}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Kolor atramentu</InputLabel>
                            <Select
                                value={selectedInkColor}
                                label="Kolor atramentu"
                                onChange={(e) => onInkColorChange(e.target.value)}
                                sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(76, 29, 149, 0.3)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(76, 29, 149, 0.5)',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#c4b5fd',
                                    }
                                }}
                            >
                                <MenuItem value="all">Wszystkie kolory</MenuItem>
                                {Object.entries(inkColors).map(([key, value]) => (
                                    <MenuItem key={key} value={key}>{value.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Set kart</InputLabel>
                            <Select
                                value={selectedSet}
                                label="Set kart"
                                onChange={(e) => onSetChange(e.target.value)}
                                sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(76, 29, 149, 0.3)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(76, 29, 149, 0.5)',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#c4b5fd',
                                    }
                                }}
                            >
                                <MenuItem value="all">Wszystkie sety</MenuItem>
                                {uniqueSets.map(set => (
                                    <MenuItem key={set} value={set}>{set}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Koszt karty</InputLabel>
                            <Select
                                value={selectedCost}
                                label="Koszt karty"
                                onChange={(e) => onCostChange(e.target.value)}
                                sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(76, 29, 149, 0.3)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(76, 29, 149, 0.5)',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#c4b5fd',
                                    }
                                }}
                            >
                                <MenuItem value="all">Wszystkie koszty</MenuItem>
                                {uniqueCosts.map(cost => (
                                    <MenuItem key={cost} value={cost.toString()}>
                                        {cost === 0 ? '0 (Bez kosztu)' : cost === '8+' ? '8+' : cost}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

export default LimitedTierlistFilters; 